export type NumericTool = 'sequence' | 'powers' | 'log' | 'exponential';
export type NumericDraft = Record<string, string>;
export type LearningStep = { title: string; formula: string; explanation: string };
export type LearningPoint = { x: number; y: number };
export const learningNumber = (value: number) => {
  if (value === 0) return '0';
  if (Number.isSafeInteger(value)) return String(value);
  if (Math.abs(value) >= 1e7 || Math.abs(value) < 0.0001)
    return value.toExponential(5).replace(/\.?(0+)(?=e)/, '');
  return value.toLocaleString('en-AU', { maximumSignificantDigits: 6, useGrouping: false });
};
export function numericLearning(tool: NumericTool, variant: string, draft: NumericDraft) {
  const read = (key: string, label: string, min: number, max: number, integer = false) => {
    const text = draft[key] ?? '';
    if (!text.trim()) throw new Error(`Enter ${label}. Your other inputs have been kept.`);
    const value = Number(text);
    if (
      !Number.isFinite(value) ||
      value < min ||
      value > max ||
      (integer && !Number.isInteger(value))
    )
      throw new Error(
        `${label} must be ${integer ? 'a whole number ' : ''}from ${min} to ${max}. Your input has been kept.`,
      );
    return value;
  };
  const n = learningNumber;
  const steps: LearningStep[] = [];
  const add = (title: string, formula: string, explanation: string) =>
    steps.push({ title, formula, explanation });
  let result: number, resultLabel: string, rule: string, interpretation: string, task: string;
  let points: LearningPoint[], rows: LearningPoint[], target: LearningPoint;
  let discrete = false,
    xLabel = 'x',
    yLabel = 'y';
  const sample = (start: number, end: number, at: (x: number) => number) =>
    Array.from({ length: 81 }, (_, i) => {
      const x = start + ((end - start) * i) / 80;
      return { x, y: at(x) };
    });
  if (tool === 'sequence') {
    const a = read('a', 'First term', -1000, 1000),
      c = read('c', variant === 'arithmetic' ? 'Difference' : 'Multiplier', -1000, 1000),
      index = read('n', 'Target term n', 1, 30, true);
    const at = (position: number) =>
      variant === 'arithmetic'
        ? a + (position - 1) * c
        : position === 1
          ? a
          : a * c ** (position - 1);
    result = at(index);
    target = { x: index, y: result };
    discrete = true;
    xLabel = 'Term n';
    yLabel = 'uₙ';
    points = Array.from({ length: Math.max(8, index) }, (_, i) => ({ x: i + 1, y: at(i + 1) }));
    rows = points;
    rule =
      variant === 'arithmetic'
        ? `uₙ = ${a} + (n − 1) × (${c})`
        : c === 0
          ? `u₁ = ${a}; uₙ = 0 for n ≥ 2 (ratio 0)`
          : `uₙ = ${a} × (${c})^(n − 1)`;
    resultLabel = `Term ${index}`;
    add(
      'Count the changes',
      `n − 1 = ${index} − 1 = ${index - 1}`,
      'The first term is at position 1. Reaching term n takes n − 1 changes, not n changes.',
    );
    if (variant === 'arithmetic') {
      add(
        'Add the accumulated difference',
        `uₙ = a + (n − 1)d = ${a} + (${index} − 1) × (${c})`,
        'An arithmetic sequence adds the same difference each time. A negative difference moves downward on the signed graph.',
      );
      add(
        'Calculate the change',
        `(${index} − 1) × (${c}) ≈ ${n((index - 1) * c)}`,
        'Multiply the number of changes by the difference, then add the first term.',
      );
      add(
        'Find and check the term',
        `u${index} ≈ ${n(a)} + (${n((index - 1) * c)}) ≈ ${n(result)}`,
        index === 1
          ? 'There are no changes yet, so the result is the first term.'
          : `Neighbour check: u${index - 1} + d ≈ ${n(at(index - 1))} + (${c}) ≈ ${n(result)}.`,
      );
    } else if (c === 0) {
      add(
        'Use the recurrence',
        `u₁ = ${a}; uₙ = uₙ₋₁ × 0 for n ≥ 2`,
        'A zero-ratio recurrence leaves the first term as entered. Every later term is zero.',
      );
      add(
        'Apply the changes',
        index === 1
          ? 'No multiplication is needed for term 1.'
          : `u₂ = ${a} × 0 = 0; later terms = 0 × 0 = 0`,
        'Use the recurrence directly. There is no need to assign a value to 0⁰.',
      );
      add(
        'Find and check the term',
        `u${index} = ${index === 1 ? a : 0}`,
        index === 1
          ? 'The target is the original first term.'
          : 'The selected term is after the first zero multiplication.',
      );
    } else {
      add(
        'Use repeated multiplication',
        `uₙ = ar^(n − 1) = ${a} × (${c})^(${index} − 1)`,
        'A geometric sequence multiplies by the same ratio each time. Brackets keep a negative ratio separate from the exponent.',
      );
      add(
        'Calculate the multiplier',
        `(${c})^${index - 1} ≈ ${n(c ** (index - 1))}`,
        index === 1
          ? 'A nonzero ratio raised to zero is 1. With no multiplications, the first term stays unchanged.'
          : c === 0
            ? 'After the first term, multiplying by zero makes every later term zero.'
            : c < 0
              ? 'Each negative multiplier reverses the sign. An even number of changes gives a positive factor; an odd number gives a negative factor.'
              : 'A positive ratio below 1 reduces magnitude; above 1 increases magnitude. A ratio of 1 preserves every term.',
      );
      add(
        'Find and check the term',
        `u${index} ≈ ${a} × (${n(c ** (index - 1))}) ≈ ${n(result)}`,
        index === 1
          ? 'With no change, u₁ equals the first term.'
          : `Recurrence check: u${index - 1} × r ≈ ${n(at(index - 1))} × (${c}) ≈ ${n(result)}.`,
      );
    }
    interpretation =
      c === 0
        ? variant === 'arithmetic'
          ? 'Zero difference: every term is the first term.'
          : 'Zero ratio: the first term stays as entered; every later term is zero.'
        : variant === 'geometric' && c < 0
          ? 'Negative ratio: signs can alternate. Points below zero represent negative terms; they are not positive bars.'
          : 'The graph shows discrete positions. Values between whole-number positions are not sequence terms.';
    task =
      'Keep the first term fixed. Change the difference or ratio, and explain how term 1 and the target term respond.';
  } else if (tool === 'log') {
    const base = read('base', 'Base', 0.01, 100),
      value = read('value', 'Positive value', 0.000001, 1000000);
    if (base === 1)
      throw new Error(
        'Base 1 is invalid: 1 raised to any real power is 1, so it cannot define an inverse logarithm. Your input has been kept.',
      );
    result = Math.log(value) / Math.log(base);
    if (Math.abs(result) > 1e6)
      throw new Error(
        'This base is too close to 1 for this view: the exponent exceeds ±1000000. Keep your input and choose a base farther from 1.',
      );
    const nearest = Math.round(result),
      isInteger = Math.abs(result - nearest) <= Number.EPSILON * 16 * Math.max(1, Math.abs(result));
    const lower = isInteger ? nearest : Math.floor(result),
      upper = isInteger ? nearest : lower + 1;
    const start = lower - 2,
      end = Math.max(upper, lower + 1) + 2;
    rows = Array.from({ length: end - start + 1 }, (_, i) => ({
      x: start + i,
      y: base ** (start + i),
    }));
    points = sample(start, end, (x) => base ** x);
    target = { x: result, y: value };
    xLabel = 'Exponent x';
    yLabel = 'bˣ';
    rule = `${base}^x = ${value} ⇔ log_${base}(${value}) = x`;
    resultLabel = 'Required exponent';
    add(
      'Translate the logarithm',
      `log_${base}(${value}) = x ⇔ ${base}^x = ${value}`,
      'A logarithm asks for an exponent. The base must be positive and different from 1; the value must be positive.',
    );
    add(
      'Locate neighbouring powers',
      isInteger
        ? `${base}^${nearest} ≈ ${n(value)}`
        : `${base}^${lower} ≈ ${n(base ** lower)}; ${base}^${upper} ≈ ${n(base ** upper)}`,
      isInteger
        ? `The target coincides with this whole-number power to numerical precision. Nearby powers are shown on both sides. ${base < 1 ? 'For a base below 1, powers decrease as the exponent increases.' : 'For a base above 1, powers increase as the exponent increases.'}`
        : `The exponent lies between ${lower} and ${upper}. ${base < 1 ? 'For a base below 1, the powers decrease as the exponent increases.' : 'For a base above 1, powers increase as the exponent increases.'} The adaptive table includes this interval, even for negative or large exponents.`,
    );
    add(
      'Use change of base',
      `x = ln(${value}) / ln(${base}) ≈ ${n(Math.log(value))} / (${n(Math.log(base))}) ≈ ${n(result)}`,
      'Use the same logarithm in numerator and denominator. Calculate from the original values; the displayed logarithms are rounded.',
    );
    add(
      'Check by exponentiation',
      `${base}^(${n(result)}) ≈ ${n(value)}`,
      'Substitute the unrounded exponent into bˣ. The displayed exponent is rounded, so this is an approximate check, not an exact equality.',
    );
    interpretation =
      base < 1
        ? 'Base between 0 and 1: both power and logarithm curves decrease. Values below 1 require positive exponents; values above 1 require negative exponents.'
        : 'Base above 1: both power and logarithm curves increase. Values below 1 require negative exponents; values above 1 require positive exponents.';
    task =
      'Try values 0.5, 1 and 32 with base 2. Then change the base to 0.5 and explain the signs of the exponents.';
  } else if (tool === 'powers') {
    const base = read('base', 'Numeric base', 0.01, 100),
      m = read('m', 'First exponent', -20, 20),
      exponent = read('p', 'Second exponent', -20, 20);
    const combined =
      variant === 'multiply' ? m + exponent : variant === 'divide' ? m - exponent : m * exponent;
    result = base ** combined;
    target = { x: combined, y: result };
    resultLabel = 'Numeric result';
    const symbol = variant === 'multiply' ? '×' : variant === 'divide' ? '÷' : 'power';
    rule =
      variant === 'power'
        ? `(${base}^(${m}))^(${exponent})`
        : `${base}^(${m}) ${symbol} ${base}^(${exponent})`;
    rows = [
      { x: m, y: base ** m },
      { x: exponent, y: base ** exponent },
      { x: combined, y: result },
    ];
    points = sample(Math.min(0, combined) - 1, Math.max(0, combined) + 1, (x) => base ** x);
    xLabel = 'Exponent x';
    yLabel = 'bˣ';
    add(
      'Keep the common base',
      rule,
      'This explorer uses a positive numeric base, so fractional and negative exponent laws stay valid in the real numbers. Division never has a zero denominator.',
    );
    add(
      'Apply the index law',
      variant === 'multiply'
        ? `bᵐ × bᵖ = b^(m + p); ${m} + (${exponent}) ≈ ${n(combined)}`
        : variant === 'divide'
          ? `bᵐ ÷ bᵖ = b^(m − p); ${m} − (${exponent}) ≈ ${n(combined)}`
          : `(bᵐ)ᵖ = b^(m × p); ${m} × (${exponent}) ≈ ${n(combined)}`,
      variant === 'multiply'
        ? 'Multiplication joins factors with the same base, so add the exponents.'
        : variant === 'divide'
          ? 'Division cancels common factors, so subtract the denominator exponent.'
          : 'The outer exponent repeats or rescales the inner power, so multiply the exponents.',
    );
    add(
      'Interpret the resulting exponent',
      combined === 0.5
        ? `${base}^(1/2) = √${base} ≈ ${n(Math.sqrt(base))}`
        : `${base}^(${n(combined)})`,
      combined === 0
        ? 'Every positive base raised to zero is 1.'
        : combined < 0
          ? `Negative exponent: take the reciprocal, 1 / ${base}^(${n(Math.abs(combined))}).`
          : Number.isInteger(combined)
            ? `For a positive whole exponent, multiply ${combined} copies of the base. The numeric calculation does not create hundreds of tiles.`
            : 'A fractional exponent represents a root or real power of this positive base; it is not a fractional count of factor tiles.',
    );
    add(
      'Calculate the numeric result',
      `${rule} ≈ ${base}^(${n(combined)}) ≈ ${n(result)}`,
      'The graph marks the combined exponent on y = bˣ. Exponent 0 gives 1; a base between 0 and 1 reverses the curve’s direction.',
    );
    interpretation = `Combined exponent ≈ ${n(combined)}. ${base === 1 ? 'Base 1 gives 1 for every exponent.' : base < 1 ? 'The power curve decreases as x increases.' : 'The power curve increases as x increases.'}`;
    task =
      'Keep the base fixed. Try a negative exponent and then a fractional exponent. Explain the reciprocal or root before checking the result.';
  } else {
    const a = read('a', 'Starting value a', -1000, 1000),
      b = read('b', 'Rate constant b', -5, 5),
      x = read('x', 'Input x', -10, 10);
    const at = (value: number) => (a === 0 ? 0 : a * Math.exp(b * value));
    result = at(x);
    target = { x, y: result };
    resultLabel = 'Function value';
    rule = `y = ${a} × e^(${b}x)`;
    const start = Math.min(0, x) - 1,
      end = Math.max(0, x) + 1;
    points = sample(start, end, at);
    rows = [...new Set([0, x / 2, x])]
      .sort((left, right) => left - right)
      .map((value) => ({ x: value, y: at(value) }));
    add(
      'Write the rule and substitute',
      `y = ae^(bx) = ${a} × e^(${b} × (${x}))`,
      'a is y when x = 0. b controls the multiplicative rate, not a fixed amount added to y.',
    );
    add(
      'Calculate the exponent',
      `b × x = ${b} × (${x}) ≈ ${n(b * x)}`,
      'Multiply b by x before applying the exponential function. e is approximately 2.71828.',
    );
    add(
      'Calculate the factor',
      `e^(${n(b * x)}) ≈ ${n(Math.exp(b * x))}`,
      'This factor is always positive. A negative starting value stays negative; a zero starting value stays zero.',
    );
    add(
      'Multiply and interpret',
      `y ≈ ${a} × (${n(Math.exp(b * x))}) ≈ ${n(result)}`,
      a === 0
        ? 'Since a = 0, y = 0 for every x, whatever b is. This is a constant zero function.'
        : b === 0
          ? `Since b = 0, e⁰ = 1 and y = ${a} for every x. This is constant, not growth or decay.`
          : `The curve ${a * b > 0 ? 'rises' : 'falls'} from left to right because a × b ${a * b > 0 ? '>' : '<'} 0. ${a < 0 ? 'With a negative starting value, direction is reversed relative to a positive starting value.' : ''}`,
    );
    interpretation =
      a === 0
        ? 'Constant zero function: a = 0.'
        : b === 0
          ? `Constant function: y = ${a}, because b = 0.`
          : `The curve ${a * b > 0 ? 'rises' : 'falls'} left to right. Magnitude ${b > 0 ? 'grows' : 'decays'} as x increases; a ${a < 0 ? 'negative' : 'positive'} starting value determines the sign.`;
    task =
      'Set a = 0, then set b = 0 with a = 3. Explain the two constant functions. Try a = −3, b = 0.5.';
  }
  if (
    !Number.isFinite(result) ||
    [...points, ...rows, target].some(
      (point) => !Number.isFinite(point.x) || !Number.isFinite(point.y),
    )
  )
    throw new Error(
      'These inputs exceed the finite numeric range of this view. Your inputs have been kept; reduce the base, exponents or ratio.',
    );
  if (tool === 'powers' && (result === 0 || points.some((point) => point.y === 0)))
    throw new Error(
      'This positive power is too small for this numeric view. Your input has been kept; reduce the exponent magnitude.',
    );
  return {
    result,
    resultLabel,
    rule,
    interpretation,
    task,
    steps,
    points,
    rows,
    target,
    discrete,
    xLabel,
    yLabel,
  };
}
